/**
 * Cognito Admin Service
 * Handles admin operations via Lambda API (when Cognito is configured)
 */

import { apiClient } from './apiClient';
import { User, UserRole } from '../types/user';

export const cognitoAdminService = {
  /**
   * Create a new user (admin only)
   */
  async createUser(username: string, email: string, password: string, role: UserRole = 'user'): Promise<User> {
    const response = await apiClient.post<{ message: string; userId: string }>(
      '/apis/v1/admin/users',
      { username, email, password, role }
    );

    if (response.error) {
      throw new Error(response.error);
    }

    // Return created user (would need to fetch full user details)
    return {
      id: response.data?.userId || '',
      username,
      email,
      role,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      isActive: true,
    };
  },

  /**
   * Get all users (admin only)
   */
  async getAllUsers(): Promise<User[]> {
    const response = await apiClient.get<{ users: User[] }>('/apis/v1/admin/users');

    if (response.error) {
      throw new Error(response.error);
    }

    return response.data?.users || [];
  },

  /**
   * Reset user password (admin only)
   */
  async resetPassword(userId: string, newPassword: string): Promise<void> {
    const response = await apiClient.post<{ message: string }>(
      `/apis/v1/admin/users/${userId}/reset-password`,
      { newPassword }
    );

    if (response.error) {
      throw new Error(response.error);
    }
  },

  /**
   * Delete user (admin only)
   */
  async deleteUser(userId: string): Promise<void> {
    const response = await apiClient.delete<{ message: string }>(
      `/apis/v1/admin/users/${userId}`
    );

    if (response.error) {
      throw new Error(response.error);
    }
  },

  /**
   * Update user (admin only)
   */
  async updateUser(userId: string, updates: Partial<User>): Promise<User> {
    const response = await apiClient.put<{ user: User; message: string }>(
      `/apis/v1/admin/users/${userId}`,
      updates
    );

    if (response.error) {
      throw new Error(response.error);
    }

    return response.data?.user || {
      id: userId,
      username: '',
      email: '',
      role: 'user',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      isActive: true,
    };
  },
};

